import {
	SettingsItemAutocomplete,
	Button,
	Icon,
	SettingsGroup,
	SettingsItemCheckbox,
	SettingsItemTextField,
} from '@elipssolution/harfang';
import { mdiPencil } from '@mdi/js';
import { Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { WidgetExternalApplicationType } from '../../../../types/widget';
import { SimulatorType } from '../../../utils/simulators';

type FormType = {
	name: string;
	isExternal?: boolean;
	isInternal?: boolean;
	isAvailableOnDesktopFormat?: boolean;
	isAvailableOnMobileFormat?: boolean;
};

type SimulatorFormProps = {
	availableSimulators: SimulatorType[];
	simulator?: WidgetExternalApplicationType;
	onSubmit: (simulator: WidgetExternalApplicationType) => void;
	onRemove: (simulator: WidgetExternalApplicationType) => void;
	onBack: () => void;
};

const SimulatorForm = ({
	simulator: initialSimulator,
	availableSimulators,
	onSubmit,
	onRemove,
	onBack,
}: SimulatorFormProps) => {
	const { onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();

	const {
		control,
		formState: { isValid, isDirty },
		handleSubmit,
		reset,
	} = useForm<FormType>();

	const defaultSimulatorForm = availableSimulators?.[0];

	const [simulator, setSimulator] = useState<WidgetExternalApplicationType>(
		initialSimulator ?? {
			key: uuid(),
			name: defaultSimulatorForm?.name,
			type: defaultSimulatorForm?.type,
		},
	);

	const {
		key,
		type,
		name: initialName,
		isInternal: isInternalInitial,
		isExternal: isExternalInitial,
		isAvailableOnMobileFormat: isAvailableOnMobileFormatInitial,
		isAvailableOnDesktopFormat: isAvailableOnDesktopFormatInitial,
	} = simulator;

	const selectedApplicationForm =
		availableSimulators.find((applicationForm) => applicationForm.type === simulator.type) ?? defaultSimulatorForm;

	const handleChangeType = (newType?: { type?: string; name: string }) =>
		setSimulator({
			key: uuid(),
			name: newType?.name ?? defaultSimulatorForm.name,
			type: newType?.type ?? defaultSimulatorForm.type,
		});

	const handleRemove = () => onRemove(simulator);

	const handleSubmitInternal = ({ name, ...rest }: FormType) => {
		onSubmit({
			key,
			type,
			name,
			...rest,
		});
	};

	const dataSource = useCallback(
		async (): Promise<{
			items: { key?: string; name: string }[];
			count: number;
		}> =>
			Promise.resolve({
				items: availableSimulators,
				count: availableSimulators.length,
			}),
		[availableSimulators],
	);

	useEffect(
		() =>
			reset({
				name: initialName,
				isInternal: isInternalInitial,
				isExternal: isExternalInitial,
				isAvailableOnMobileFormat: isAvailableOnMobileFormatInitial,
				isAvailableOnDesktopFormat: isAvailableOnDesktopFormatInitial,
			}),
		[
			initialName,
			isExternalInitial,
			isInternalInitial,
			isAvailableOnDesktopFormatInitial,
			isAvailableOnMobileFormatInitial,
			reset,
		],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${initialSimulator ? 'modification' : 'création'} du simulateur ?`,
		);
	}, [isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange, initialSimulator]);

	return (
		<SettingsDialogPage title={simulator.name} onBack={onBack}>
			<SettingsItemAutocomplete<{ type?: string; name: string }>
				dataSource={dataSource}
				dataKey="type"
				description="Liste des simulateurs disponibles."
				getOptionLabel={({ name }) => name}
				label="Type de simulateur"
				onChange={handleChangeType}
				value={selectedApplicationForm}
				disableClearable
			/>

			<SettingsGroup label="Configuration">
				<Controller
					name="name"
					control={control}
					render={({ field }) => (
						<SettingsItemTextField {...field} description="Nom du simulateur." label="Nom" required />
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="isInternal"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, les utilisateurs cabinet auront accès à ce simulateur."
							label="Visible cabinet"
						/>
					)}
				/>
				<Controller
					name="isExternal"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, les utilisateurs entreprise auront accès à ce simulateur."
							label="Visible entreprise"
						/>
					)}
				/>
				<Controller
					name="isAvailableOnDesktopFormat"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le simulateur sera visible sur ordinateur."
							label="Visible version ordinateur"
						/>
					)}
				/>
				<Controller
					name="isAvailableOnMobileFormat"
					control={control}
					defaultValue={false}
					render={({ field: { value, ...field } }) => (
						<SettingsItemCheckbox
							{...field}
							checked={value}
							description="Si coché, le simulateur sera visible sur mobile."
							label="Visible version mobile"
						/>
					)}
				/>
			</SettingsGroup>

			<Stack direction="row-reverse" justifyContent="space-between" gap={1}>
				<Button
					disabled={!isValid}
					startIcon={<Icon path={mdiPencil} size="small" />}
					variant="contained"
					onClick={handleSubmit(handleSubmitInternal)}
				>
					{!initialSimulator ? 'Ajouter' : 'Modifier'}
				</Button>

				{!!initialSimulator && (
					<Button variant="outlined" color="error" onClick={handleRemove}>
						Supprimer
					</Button>
				)}
			</Stack>
		</SettingsDialogPage>
	);
};

export default SimulatorForm;
